import React from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, Clock, Layers, XCircle } from 'lucide-react';
import { RunInstance } from '../../types';

export type OverlappingRunAction = 'wait' | 'parallel' | 'cancel';

interface OverlappingRunsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAction: (action: OverlappingRunAction) => void;
  activeRuns: RunInstance[];
  workflowName: string;
}

/**
 * AC-465: Overlapping Runs Confirmation Dialog
 *
 * Displays when attempting to start a queue-consuming workflow that
 * already has active runs. Provides three options:
 * - Wait: Queue the run to start after current completes
 * - Run Parallel: Start now, processing different queue items
 * - Cancel: Don't start the run
 */
const OverlappingRunsDialog: React.FC<OverlappingRunsDialogProps> = ({
  isOpen,
  onClose,
  onAction,
  activeRuns,
  workflowName,
}) => {
  if (!isOpen) return null;

  const handleAction = (action: OverlappingRunAction) => {
    onAction(action);
    if (action === 'cancel') {
      onClose();
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'running':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'paused':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Active Run Detected</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:text-gray-400"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            <span className="font-medium text-gray-900 dark:text-warm-white">{workflowName}</span> already has{' '}
            {activeRuns.length === 1 ? 'an active run' : `${activeRuns.length} active runs`}.
            How would you like to proceed?
          </p>

          {/* Active Runs List */}
          <div className="mb-6 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Active Runs</h4>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {activeRuns.map((run) => (
                <div
                  key={run.run_instance_id}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-gray-700 dark:text-gray-300">Run #{run.run_instance_id}</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                      run.status
                    )}`}
                  >
                    {run.status}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Action Options */}
          <div className="space-y-3">
            <button
              onClick={() => handleAction('wait')}
              className="w-full flex items-start p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 transition-colors text-left group"
            >
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-4 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                <Clock className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-warm-white">Wait in Queue</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Queue this run to start automatically after the current run completes.
                </p>
              </div>
            </button>

            <button
              onClick={() => handleAction('parallel')}
              className="w-full flex items-start p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 transition-colors text-left group"
            >
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg mr-4 group-hover:bg-green-200 dark:group-hover:bg-green-900/50 transition-colors">
                <Layers className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-warm-white">Run in Parallel</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Start now and process different queue items concurrently.
                </p>
              </div>
            </button>

            <button
              onClick={() => handleAction('cancel')}
              className="w-full flex items-start p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 transition-colors text-left group"
            >
              <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg mr-4 group-hover:bg-gray-200 dark:group-hover:bg-gray-600 transition-colors">
                <XCircle className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-warm-white">Cancel</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Don't start a new run at this time.
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default OverlappingRunsDialog;
